import React, { useRef, useState } from 'react'
import { Translate } from 'react-localize-redux'
import PropTypes from 'prop-types'
import { CSVLink } from 'react-csv'
import { Paper, Tabs, Tab, Toolbar, Button, Box, Grid } from '@material-ui/core'
import SaveAltIcon from '@material-ui/icons/SaveAlt'
import TeaserChip from 'components/Teaser/TeaserChip'
import useTeamFeature from 'hooks/useTeamFeature'
import UpgradeMembershipOverlay from 'components/UpgradeMembership/UpgradeMembershipOverlay'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import { useStyles } from './components/style/styled'

import {
  PlayersSkills,
  PlayersStatistics,
  PlayersAttendance,
  PlayersPersonalData,
} from './components'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const TeamPlayersDataContainer = ({ theme }) => {
  const classes = useStyles(theme)
  const tabs = ['stats', 'personal', 'skills']
  const [value, setValue] = useState(tabs[0])
  const csvLinkRef = useRef(null)
  const [downloadReady, setDownloadReady] = useState(false)

  const { enabled: hasPlayerStatisticsEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.player_statistics_enabled
  )
  const { enabled: hasPlayerTrainingAttendanceEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.player_attendance_enabled
  )
  const { enabled: hasTeamSkillsEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.team_skills_enabled
  )
  const { enabled: hasPlayersPersonalDataExportEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.players_personal_data_export
  )

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  function sortCsvDataByPlayer(object1, object2) {
    if (object1.Player < object2.Player) return -1
    if (object1.Player > object2.Player) return 1

    return 0
  }
  const [csvData, setCsvData] = useState([])

  const handleExport = () => {
    // If player statistics are not enabled, do not allow export
    if (!hasPlayerStatisticsEnabled) return

    setDownloadReady(true) // this will mount the link

    // setTimeout is used to ensure the link is ready before clicking
    setTimeout(() => {
      csvLinkRef.current.link.click()
      setDownloadReady(false) // unmount after download
    }, 0)
  }

  return (
    <Paper className={classes.paper}>
      <Toolbar className={classes.root}>
        <Tabs
          value={value}
          className={classes.title}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Translate id="tab.team-players-statistics" />{' '}
                {!hasPlayerStatisticsEnabled && <TeaserChip />}
              </Grid>
            }
            value="stats"
          />
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Translate id="tab.team-players-attendance" />
                {!hasPlayerTrainingAttendanceEnabled && <TeaserChip />}
              </Grid>
            }
            value="attendance"
          />
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Translate id="tab.team-players-player-data" />
                {!hasPlayersPersonalDataExportEnabled && <TeaserChip />}
              </Grid>
            }
            value="personal"
          />
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Translate id="tab.team-players-skills" />
                {!hasTeamSkillsEnabled && <TeaserChip />}
              </Grid>
            }
            value="skills"
          />
          <Box ml="auto">
            {value === 'stats' && (
              <>
                {downloadReady && (
                  <CSVLink
                    data={csvData ? csvData.sort(sortCsvDataByPlayer) : null}
                    className="hidden"
                    ref={csvLinkRef}
                  />
                )}
                <Button
                  variant="contained"
                  className={classes.csvButton}
                  size="small"
                  startIcon={<SaveAltIcon size="small" />}
                  color="primary"
                  onClick={handleExport}
                  endIcon={
                    !hasPlayerStatisticsEnabled ? (
                      <LockIconComponent context="primaryButton" />
                    ) : null
                  }
                >
                  <Translate id="button.export" />
                </Button>
              </>
            )}
          </Box>
        </Tabs>
      </Toolbar>
      {value === 'stats' && (
        <Box style={{ position: 'relative' }}>
          {!hasPlayerStatisticsEnabled && <UpgradeMembershipOverlay />}
          <PlayersStatistics setCsvData={setCsvData} />
        </Box>
      )}
      {value === 'attendance' && (
        <Box style={{ position: 'relative' }}>
          {!hasPlayerTrainingAttendanceEnabled && <UpgradeMembershipOverlay />}
          <PlayersAttendance />
        </Box>
      )}
      {value === 'personal' && (
        <Box style={{ position: 'relative' }}>
          {!hasPlayersPersonalDataExportEnabled && <UpgradeMembershipOverlay />}
          <PlayersPersonalData />
        </Box>
      )}
      {value === 'skills' && (
        <Box style={{ position: 'relative' }}>
          {!hasTeamSkillsEnabled && <UpgradeMembershipOverlay />}
          <PlayersSkills />
        </Box>
      )}
    </Paper>
  )
}
TeamPlayersDataContainer.defaultProps = {
  theme: {},
}

TeamPlayersDataContainer.propTypes = {
  theme: PropTypes.oneOfType([
    PropTypes.shape().isRequired,
    PropTypes.oneOf([null]).isRequired,
  ]),
}

export default TeamPlayersDataContainer
